import { useEffect, useMemo, useRef, useState } from 'react';
import { Shell } from '../shell/Shell';
import { I, StatusDot, PageHeader, FilterBar, usePagination, Pager } from '../atoms';
import { useToasts, Toasts } from '../Toasts';
import { usePlanningPermissions } from '../usePlanningPermissions';
import { errMessage } from '../pageUtils';
import { listUsers, listUserDirectory, adjustTokens, inviteUser, revokeUser, setUserAiMode, type AiMode, type PlanningUser, setUserAdministrator } from '../api/users';
import { refreshMockMode } from '../mockMode';
import { TERMS_VERSION } from '../terms';

const STEPS = [-100, -10, -1, 1, 10, 100];
// Clicks within this window are added together and saved as one adjustment.
const SAVE_DELAY_MS = 800;

function statusKind(status: string): string {
  if (status === 'CONFIRMED') return 'active';
  if (status === 'FORCE_CHANGE_PASSWORD') return 'pending';
  if (status === 'DISABLED') return 'inactive';
  return 'warn';
}

function statusLabel(status: string): string {
  if (status === 'CONFIRMED') return 'Active';
  if (status === 'FORCE_CHANGE_PASSWORD') return 'Invited';
  if (status === 'DISABLED') return 'Disabled';
  return status;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Which Terms of Use and Privacy Notice version the person accepted and when; an older version is flagged, since
// they'll be asked to accept the current one at their next sign-in.
function TermsCell({ accepted }: { accepted: { version: string; at: string } | undefined }) {
  if (!accepted) return <span style={{ color: 'var(--ink-3)' }}>Not yet</span>;
  const current = accepted.version === TERMS_VERSION;
  return (
    <span>
      Version {accepted.version}
      {!current && <span style={{ color: 'var(--warn, var(--ink-3))' }}> (current is {TERMS_VERSION})</span>}
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{fmtDate(accepted.at)}</div>
    </span>
  );
}

// The same on one line, for the phone layout's card.
function termsLine(accepted: { version: string; at: string } | undefined): string {
  if (!accepted) return 'Terms not yet accepted';
  const old = accepted.version === TERMS_VERSION ? '' : ` (current is ${TERMS_VERSION})`;
  return `Terms version ${accepted.version}${old}, accepted ${fmtDate(accepted.at)}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function InviteDrawer({ onClose, onInvited }: { onClose: () => void; onInvited: (email: string, emailSent: boolean) => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mock, setMock] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    const f = firstName.trim(), l = lastName.trim(), e = email.trim();
    if (!f || !l) { setErr('First and last name are required.'); return; }
    if (!EMAIL_RE.test(e)) { setErr('Enter a valid email address.'); return; }
    setSaving(true); setErr(null);
    try {
      const { emailSent } = await inviteUser(e, f, l, mock);
      onInvited(e, emailSent);
      onClose();
    } catch (ex) {
      setErr(errMessage(ex, 'Could not send the invitation. Please try again.'));
      setSaving(false);
    }
  }

  return (
    <>
      <div className="drawer-overlay" onClick={saving ? undefined : onClose} />
      <div className="drawer" role="dialog" aria-label="Invite user">
        <div className="drawer-hd">
          <span className="drawer-hd-title">Invite user</span>
          <button className="btn btn-icon" onClick={onClose} disabled={saving} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 1l12 12M13 1L1 13" /></svg>
          </button>
        </div>
        <div className="drawer-body">
          <div className="field-row">
            <div className="field">
              <label htmlFor="inv-first">First name</label>
              <input id="inv-first" value={firstName} onChange={e => setFirstName(e.target.value)} autoFocus autoComplete="off" />
            </div>
            <div className="field">
              <label htmlFor="inv-last">Last name</label>
              <input id="inv-last" value={lastName} onChange={e => setLastName(e.target.value)} autoComplete="off" />
            </div>
          </div>
          <div className="field">
            <label htmlFor="inv-email">Email</label>
            <input id="inv-email" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="off" />
          </div>
          <div className="field">
            <label htmlFor="inv-model">Model</label>
            <select id="inv-model" value={mock ? 'mock' : 'live'} onChange={e => setMock(e.target.value === 'mock')}>
              <option value="mock">Mock — canned examples, nothing is charged</option>
              <option value="live">Live — real, paid model calls</option>
            </select>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
            They receive an email with a temporary password, and start with 10 tokens.
          </div>
          {err && <div role="alert" style={{ color: 'var(--neg)', fontSize: 12.5 }}>{err}</div>}
        </div>
        <div className="drawer-ft">
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </div>
    </>
  );
}

export function UsersPage() {
  const { can, userId: myUserId, loading: permLoading } = usePlanningPermissions();
  const { toasts, addToast, removeToast } = useToasts();
  const canWrite = can('users:write');
  const allowed = can('users:read');

  const [users, setUsers] = useState<PlanningUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [balances, setBalances] = useState<Map<string, number>>(new Map());
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [profiles, setProfiles] = useState<Map<string, { forename: string; surname: string }>>(new Map());
  const [aiModes, setAiModes] = useState<Map<string, AiMode>>(new Map());
  const [administrators, setAdministrators] = useState<Set<string>>(new Set());
  const [termsAccepted, setTermsAccepted] = useState<Map<string, { version: string; at: string }>>(new Map());
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [balancesError, setBalancesError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [pending, setPending] = useState<Map<string, number>>(new Map());
  const pendingRef = useRef<Map<string, number>>(new Map());
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const inFlight = useRef<Set<string>>(new Set());
  const flushRef = useRef<(userId: string) => void>(() => {});
  const [showInvite, setShowInvite] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setUsersLoading(true); setUsersError(null);
    listUsers()
      .then(list => { if (!cancelled) setUsers(list); })
      .catch((e: Error) => { if (!cancelled) setUsersError(e.message); })
      .finally(() => { if (!cancelled) setUsersLoading(false); });
    return () => { cancelled = true; };
  }, [allowed, reloadTick]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setBalancesLoading(true); setBalancesError(null);
    listUserDirectory()
      .then(d => { if (!cancelled) { setBalances(d.balances); setNames(d.names); setProfiles(d.profiles); setAiModes(d.aiModes); setAdministrators(d.administrators); setTermsAccepted(d.termsAccepted); } })
      .catch((e: unknown) => { if (!cancelled) setBalancesError(errMessage(e, 'Could not load token balances.')); })
      .finally(() => { if (!cancelled) setBalancesLoading(false); });
    return () => { cancelled = true; };
  }, [allowed, reloadTick]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return users;
    return users.filter(u => u.email.toLowerCase().includes(n) || (names.get(u.userId) ?? '').toLowerCase().includes(n));
  }, [users, names, q]);
  const pg = usePagination(filtered);

  function setPendingFor(userId: string, delta: number) {
    if (delta === 0) pendingRef.current.delete(userId); else pendingRef.current.set(userId, delta);
    setPending(new Map(pendingRef.current));
  }

  async function flush(userId: string) {
    timers.current.delete(userId);
    const delta = pendingRef.current.get(userId) ?? 0;
    if (delta === 0) { setPendingFor(userId, 0); return; }
    if (inFlight.current.has(userId)) return;
    inFlight.current.add(userId);
    try {
      await adjustTokens(userId, delta);
      setBalances(prev => new Map(prev).set(userId, (prev.get(userId) ?? 0) + delta));
      setPendingFor(userId, (pendingRef.current.get(userId) ?? 0) - delta);
    } catch (e) {
      setPendingFor(userId, (pendingRef.current.get(userId) ?? 0) - delta);
      addToast('error', errMessage(e, 'Could not change the token balance.'));
    } finally {
      inFlight.current.delete(userId);
      if ((pendingRef.current.get(userId) ?? 0) !== 0 && !timers.current.has(userId)) flushRef.current(userId);
    }
  }
  flushRef.current = (id) => { void flush(id); };

  function adjust(userId: string, step: number) {
    // A removal bigger than what's left takes the balance to 0 rather than below it.
    const current = (balances.get(userId) ?? 0) + (pendingRef.current.get(userId) ?? 0);
    const applied = Math.max(step, -current);
    if (applied === 0) return;
    setPendingFor(userId, (pendingRef.current.get(userId) ?? 0) + applied);
    const existing = timers.current.get(userId);
    if (existing) clearTimeout(existing);
    timers.current.set(userId, setTimeout(() => flushRef.current(userId), SAVE_DELAY_MS));
  }

  function markBusy(userId: string, on: boolean) {
    setBusy(prev => { const next = new Set(prev); if (on) next.add(userId); else next.delete(userId); return next; });
  }

  function notifyInvited(email: string, emailSent: boolean) {
    if (emailSent) addToast('success', `Invitation sent to ${email}`);
    else addToast('info', `${email} already has an account, so no email was sent. They have access again and can sign in with their existing password.`, true);
  }

  async function resend(u: PlanningUser) {
    const p = profiles.get(u.userId);
    if (!p?.forename || !p.surname) return;
    if (!window.confirm(`Send ${u.email} a new invitation? Their token balance goes back to 10.`)) return;
    markBusy(u.userId, true);
    try {
      const { emailSent } = await inviteUser(u.email, p.forename, p.surname, (aiModes.get(u.userId) ?? 'Mock') !== 'Live');
      notifyInvited(u.email, emailSent);
      setReloadTick(t => t + 1);
    } catch (e) {
      addToast('error', errMessage(e, 'Could not re-send the invitation.'));
    } finally {
      markBusy(u.userId, false);
    }
  }

  async function changeAiMode(u: PlanningUser, mode: AiMode) {
    const before = aiModes.get(u.userId);
    setAiModes(prev => new Map(prev).set(u.userId, mode));
    markBusy(u.userId, true);
    try {
      await setUserAiMode(u.userId, mode);
      if (u.userId === myUserId) void refreshMockMode();
    } catch (e) {
      setAiModes(prev => { const next = new Map(prev); if (before === undefined) next.delete(u.userId); else next.set(u.userId, before); return next; });
      addToast('error', errMessage(e, 'Could not change the model setting.'));
    } finally {
      markBusy(u.userId, false);
    }
  }

  async function changeAdministrator(u: PlanningUser, administrator: boolean) {
    const who = names.get(u.userId) ?? u.email;
    if (!window.confirm(administrator
      ? `Make ${who} an administrator? They will be able to manage users, tokens, settings and policy inputs.`
      : `Remove administrator rights from ${who}?`)) return;
    setAdministrators(prev => { const next = new Set(prev); if (administrator) next.add(u.userId); else next.delete(u.userId); return next; });
    markBusy(u.userId, true);
    try {
      await setUserAdministrator(u.userId, administrator);
    } catch (e) {
      setAdministrators(prev => { const next = new Set(prev); if (administrator) next.delete(u.userId); else next.add(u.userId); return next; });
      addToast('error', errMessage(e, 'Could not change administrator rights.'));
    } finally {
      markBusy(u.userId, false);
    }
  }

  async function revoke(u: PlanningUser) {
    if (!window.confirm(`Revoke access for ${names.get(u.userId) ?? u.email}? They will no longer be able to use Elevations Planning. Their cases and token balance are kept.`)) return;
    markBusy(u.userId, true);
    try {
      await revokeUser(u.userId);
      addToast('success', `Access revoked for ${u.email}`);
      setReloadTick(t => t + 1);
    } catch (e) {
      addToast('error', errMessage(e, 'Could not revoke access.'));
    } finally {
      markBusy(u.userId, false);
    }
  }

  useEffect(() => () => {
    const t = timers.current;
    for (const [userId, timer] of t) { clearTimeout(timer); flushRef.current(userId); }
  }, []);

  if (!permLoading && !allowed) {
    return (
      <Shell current="users" crumbs={[{ label: 'Administration' }, { label: 'Users' }]}>
        <div className="card">
          <div className="card-body" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
            You don&apos;t have permission to view this page.
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell current="users" crumbs={[{ label: 'Administration' }, { label: 'Users' }]}>
      <PageHeader
        title="Users"
        subtitle="Who can sign in, and how many assessment tokens each person has left."
        actions={canWrite && <button className="btn btn-primary" onClick={() => setShowInvite(true)}>Invite user</button>}
      />

      <FilterBar>
        <div className="filter-search">
          {I.search}
          <input placeholder="Search name or email…" value={q} onChange={e => setQ(e.target.value)} aria-label="Search users" />
        </div>
      </FilterBar>

      <div className="card">
        <div className="card-body flush">
          {balancesError && !usersError && (
            <div style={{ padding: '10px 16px', color: 'var(--neg)', fontSize: 12.5 }}>Failed to load tokens: {balancesError}</div>
          )}
          {usersError ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--neg)', fontSize: 12.5 }}>Failed to load users: {usersError}</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="tbl users-tbl">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th className="hide-mobile">Status</th>
                      <th className="hide-mobile">Terms accepted</th>
                      <th className="num">Tokens</th>
                      <th>Model</th>
                      {canWrite && <th>Administrator</th>}
                      {canWrite && <th className="right">Adjust</th>}
                      {canWrite && <th className="right">Access</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      Array.from({ length: 4 }, (_, i) => (
                        <tr key={i}>
                          <td><span className="skel" style={{ width: 180 }} /></td>
                          <td className="hide-mobile"><span className="skel" style={{ width: 60 }} /></td>
                          <td className="hide-mobile"><span className="skel" style={{ width: 80 }} /></td>
                          <td className="num"><span className="skel" style={{ width: 36 }} /></td>
                          <td><span className="skel" style={{ width: 64 }} /></td>
                          {canWrite && <td><span className="skel" style={{ width: 90 }} /></td>}
                          {canWrite && <td className="right"><span className="skel" style={{ width: 240 }} /></td>}
                          {canWrite && <td className="right"><span className="skel" style={{ width: 120 }} /></td>}
                        </tr>
                      ))
                    ) : pg.pageItems.length === 0 ? (
                      <tr><td colSpan={canWrite ? 8 : 5} style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', cursor: 'default' }}>
                        {q.trim() ? 'No users match your search.' : 'No users yet.'}
                      </td></tr>
                    ) : pg.pageItems.map(u => {
                      const saved = balances.get(u.userId) ?? 0;
                      const change = pending.get(u.userId) ?? 0;
                      const balance = saved + change;
                      return (
                        <tr key={u.userId} style={{ cursor: 'default' }}>
                          <td className="primary">
                            {names.get(u.userId) ?? u.email}
                            {names.has(u.userId) && <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--ink-3)' }}>{u.email}</div>}
                            <span className="users-status-inline" style={{ alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 400, color: 'var(--ink-3)' }}>
                              <StatusDot kind={statusKind(u.status)} />{statusLabel(u.status)}
                            </span>
                            {!balancesLoading && (
                              <span className="users-terms-inline" style={{ fontSize: 12, fontWeight: 400, color: 'var(--ink-3)' }}>
                                {termsLine(termsAccepted.get(u.userId))}
                              </span>
                            )}
                          </td>
                          <td className="hide-mobile">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <StatusDot kind={statusKind(u.status)} />{statusLabel(u.status)}
                            </span>
                          </td>
                          <td className="hide-mobile">
                            {balancesLoading ? <span className="skel" style={{ width: 80 }} /> : <TermsCell accepted={termsAccepted.get(u.userId)} />}
                          </td>
                          <td className="num users-tokens">{balancesLoading ? <span className="skel" style={{ width: 36 }} /> : (
                            <span style={change !== 0 ? { color: 'var(--ink-3)', fontStyle: 'italic' } : undefined} title={change !== 0 ? 'Saving…' : undefined}>{balance}</span>
                          )}</td>
                          <td>
                            {balancesLoading ? <span className="skel" style={{ width: 64 }} /> : canWrite ? (
                              <select
                                aria-label={`Model for ${names.get(u.userId) ?? u.email}`}
                                value={aiModes.get(u.userId) ?? 'Mock'}
                                disabled={busy.has(u.userId) || !!balancesError}
                                onChange={e => void changeAiMode(u, e.target.value as AiMode)}
                              >
                                <option value="Mock">Mock</option>
                                <option value="Live">Live</option>
                                <option value="Dev" title="Assessments show the text the model would be sent, to paste into a Claude chat, and take its reply back">Dev</option>
                              </select>
                            ) : (aiModes.get(u.userId) ?? 'Mock')}
                          </td>
                          {canWrite && (
                            <td className="users-admin">
                              {balancesLoading ? <span className="skel" style={{ width: 90 }} /> : (
                                <label
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: u.userId === myUserId ? 'default' : 'pointer' }}
                                  title={u.userId === myUserId ? "You can't change your own administrator role" : undefined}
                                >
                                  <input
                                    type="checkbox"
                                    checked={administrators.has(u.userId)}
                                    disabled={u.userId === myUserId || busy.has(u.userId) || !!balancesError}
                                    onChange={e => void changeAdministrator(u, e.target.checked)}
                                  />
                                  Administrator
                                </label>
                              )}
                            </td>
                          )}
                          {canWrite && (
                            <td className="right users-adjust">
                              <span className="users-adjust-btns" style={{ display: 'inline-flex', gap: 4, justifyContent: 'flex-end' }}>
                                {STEPS.map(step => (
                                  <button
                                    key={step}
                                    className={`btn users-step ${step < 0 ? 'users-step-neg' : 'users-step-pos'}`}
                                    style={{ padding: '0 8px', minWidth: 44 }}
                                    disabled={balancesLoading || !!balancesError || (step < 0 && balance <= 0)}
                                    onClick={() => void adjust(u.userId, step)}
                                    aria-label={`${step > 0 ? 'Add' : 'Remove'} ${Math.abs(step)} token${Math.abs(step) === 1 ? '' : 's'} for ${names.get(u.userId) ?? u.email}`}
                                  >
                                    {step > 0 ? `+${step}` : step}
                                  </button>
                                ))}
                              </span>
                            </td>
                          )}
                          {canWrite && (
                            <td className="right users-access">
                              <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'flex-end' }}>
                                {u.status === 'FORCE_CHANGE_PASSWORD' && (
                                  <button
                                    className="btn"
                                    disabled={busy.has(u.userId) || !profiles.get(u.userId)?.forename || !profiles.get(u.userId)?.surname}
                                    title={profiles.get(u.userId)?.forename ? 'Email a new temporary password and reset tokens to 10' : 'No name on file to re-invite with'}
                                    onClick={() => void resend(u)}
                                  >
                                    Resend invite
                                  </button>
                                )}
                                {u.userId !== myUserId && (
                                  <button className="btn btn-danger" disabled={busy.has(u.userId)} onClick={() => void revoke(u)}>
                                    Revoke
                                  </button>
                                )}
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pager page={pg.page} totalPages={pg.totalPages} hasPrev={pg.hasPrev} hasNext={pg.hasNext} prev={pg.prev} next={pg.next} count={filtered.length} />
            </>
          )}
        </div>
      </div>

      {showInvite && (
        <InviteDrawer
          onClose={() => setShowInvite(false)}
          onInvited={(email, emailSent) => { notifyInvited(email, emailSent); setReloadTick(t => t + 1); }}
        />
      )}
      <Toasts items={toasts} onRemove={removeToast} />
    </Shell>
  );
}
